
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StrategyDetails from "@/components/follow-strategy/StrategyDetails";
import { supabase } from "@/integrations/supabase/client";
import StrategyNotFound from "@/components/follow-strategy/StrategyNotFound";
import StrategyLoading from "@/components/follow-strategy/StrategyLoading";
import PremiumSubscriptionBenefits from "@/components/follow-strategy/PremiumSubscriptionBenefits";
import PremiumExistingAction from "@/components/follow-strategy/PremiumExistingAction";
import { enrichStrategyData } from "@/utils/strategyDataUtils";
import ChatOption from "@/components/ChatOption";
import { useIsMobile } from "@/hooks/use-mobile";

const FollowStrategyPremiumExisting = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState<any>(null);

  const isMobile = useIsMobile();

  const strategy = location.state?.strategy;
  const filters = location.state?.filters;
  const originalStrategyId = location.state?.originalStrategyId;

  const advantages = [
    "Instant Access – Follow strategies immediately with your active subscription.",
    "No Additional Costs – Your premium subscription covers full access to all strategy signals.",
    "Priority Support – Get expert assistance when needed with premium trading strategies.",
    "Diversify Your Portfolio – Follow multiple strategies to spread risk and maximize returns."
  ];

  useEffect(() => {
    const checkUserAndSubscription = async () => {
      try {
        setIsLoading(true);

        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (userError || !user) {
          console.error("Error fetching user:", userError);
          toast({
            title: "Authentication required",
            description: "Please log in to follow premium strategies",
            variant: "destructive"
          });
          navigate("/auth");
          return;
        }

        setUserData(user);

        const { data: subscriptions, error } = await supabase
          .from('strategy_subscriptions')
          .select('*')
          .eq('user_id', user.id);

        if (error) {
          console.error("Error checking subscription:", error);
          toast({
            title: "Error",
            description: "Could not verify subscription status",
            variant: "destructive"
          });
          navigate('/marketplace');
          return;
        }

        // Check if there's an active subscription or one with a future billing date
        const currentDate = new Date();
        const hasValidSubscription = subscriptions?.some(sub => {
          const nextBillingDate = sub.next_billing_date ? new Date(sub.next_billing_date) : null;
          return sub.active || (nextBillingDate && currentDate < nextBillingDate);
        });

        if (!hasValidSubscription) {
          console.log("No active subscription found, redirecting to regular follow page");
          navigate('/follow-strategy', { state: { strategy } });
          return;
        }
      } catch (err) {
        console.error("Unexpected error checking user status:", err);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive"
        });
        navigate('/marketplace');
      } finally {
        setIsLoading(false);
      }
    };

    checkUserAndSubscription();
  }, [navigate, toast, strategy]);

  if (isLoading) {
    return <StrategyLoading />;
  }

  if (!strategy) {
    return <StrategyNotFound filters={filters} />;
  }

  // No need to apply the utility function if the data is already correctly formatted
  // Just ensure isPremium is set correctly if it wasn't in the incoming data
  const enrichedStrategy = {
    ...strategy,
    isPremium: strategy.isPremium !== undefined ? strategy.isPremium : true
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E7F0FD] to-white">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <button
          onClick={() => navigate('/marketplace', { state: { filters } })}
          className="flex items-center text-[#517FA4] hover:text-[#243949] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-bold text-[#243949] mb-8">
            Follow {enrichedStrategy.name}
          </h1>

          <div className="grid md:grid-cols-2 gap-12">
            <StrategyDetails strategy={enrichedStrategy} />

            <div>
              <h2 className="text-2xl font-semibold mb-6 text-[#243949]">Premium Subscription Benefits</h2>

              <PremiumSubscriptionBenefits advantages={advantages} />

              <PremiumExistingAction strategy={enrichedStrategy} advantages={advantages} />
            </div>
          </div>
        </div>
      </main>
      {/* Only show ChatOption on desktop */}
      {!isMobile && (
        <ChatOption />
      )}
      <Footer />
    </div>
  );
};

export default FollowStrategyPremiumExisting;
